import React, { useState } from "react";
import { Box, Checkbox, IconButton, InputBase, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import { Add, Close, DragIndicator } from "@mui/icons-material";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChecklistItem, taskService } from "../../../services/taskService";
import { useTaskStore } from "../TaskContext";
import { apiErrorMessage } from "../taskUtils";

interface ChecklistProps {
    taskId: string;
    items: ChecklistItem[];
    canEdit: boolean;
    onChange: (items: ChecklistItem[]) => void;
}

export const Checklist: React.FC<ChecklistProps> = ({ taskId, items, canEdit, onChange }) => {
    const { toast } = useTaskStore();
    const [draft, setDraft] = useState("");
    const [adding, setAdding] = useState(false);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    const done = items.filter((i) => i.is_done).length;
    const fail = (err: unknown, fallback: string) => toast({ message: apiErrorMessage(err, fallback), severity: "error" });

    const addItems = async (texts: string[]) => {
        const clean = texts.map((t) => t.trim()).filter(Boolean).map((t) => t.slice(0, 300));
        if (!clean.length) return;
        setAdding(true);
        let next = items;
        try {
            for (const text of clean) {
                const item = await taskService.addChecklistItem(taskId, text);
                next = [...next, item];
                onChange(next);
            }
        } catch (err) {
            fail(err, "Couldn't add that item.");
        } finally {
            setAdding(false);
        }
    };

    const toggle = async (item: ChecklistItem) => {
        const optimistic = items.map((i) => (i.item_id === item.item_id ? { ...i, is_done: !i.is_done } : i));
        onChange(optimistic);
        try {
            const saved = await taskService.updateChecklistItem(item.item_id, { is_done: !item.is_done });
            onChange(optimistic.map((i) => (i.item_id === saved.item_id ? saved : i)));
        } catch (err) {
            onChange(items);
            fail(err, "Couldn't update the checklist.");
        }
    };

    const rename = async (item: ChecklistItem, text: string) => {
        const clean = text.trim();
        if (!clean || clean === item.text) return;
        onChange(items.map((i) => (i.item_id === item.item_id ? { ...i, text: clean } : i)));
        try {
            await taskService.updateChecklistItem(item.item_id, { text: clean.slice(0, 300) });
        } catch (err) {
            onChange(items);
            fail(err, "Couldn't rename that item.");
        }
    };

    const remove = async (item: ChecklistItem) => {
        onChange(items.filter((i) => i.item_id !== item.item_id));
        try {
            await taskService.deleteChecklistItem(item.item_id);
        } catch (err) {
            onChange(items);
            fail(err, "Couldn't remove that item.");
        }
    };

    const onDragEnd = async ({ active, over }: DragEndEvent) => {
        if (!over || active.id === over.id) return;
        const from = items.findIndex((i) => i.item_id === active.id);
        const to = items.findIndex((i) => i.item_id === over.id);
        const next = arrayMove(items, from, to);
        onChange(next);
        try {
            await taskService.reorderChecklist(taskId, next.map((i) => i.item_id));
        } catch (err) {
            onChange(items);
            fail(err, "Couldn't reorder the checklist.");
        }
    };

    if (!canEdit && items.length === 0) return null;

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Checklist
                </Typography>
                {items.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {done}/{items.length}
                    </Typography>
                )}
            </Stack>
            {items.length > 0 && (
                <LinearProgress
                    variant="determinate"
                    value={(done / items.length) * 100}
                    color={done === items.length ? "success" : "primary"}
                    sx={{ height: 4, borderRadius: 2, mb: 1, bgcolor: "#eef2f1" }}
                />
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={items.map((i) => i.item_id)} strategy={verticalListSortingStrategy}>
                    {items.map((item) => (
                        <ChecklistRow
                            key={item.item_id}
                            item={item}
                            canEdit={canEdit}
                            onToggle={() => toggle(item)}
                            onRename={(text) => rename(item, text)}
                            onRemove={() => remove(item)}
                        />
                    ))}
                </SortableContext>
            </DndContext>
            {canEdit && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 3.5, pr: 1, py: 0.25, color: "text.secondary" }}>
                    <Add fontSize="small" />
                    <InputBase
                        fullWidth
                        placeholder="Add an item — Enter to add, paste a list to add many"
                        value={draft}
                        disabled={adding}
                        onChange={(e) => setDraft(e.target.value)}
                        onPaste={(e) => {
                            const text = e.clipboardData.getData("text");
                            const lines = text.split(/\r?\n/).map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, "").trim()).filter(Boolean);
                            if (lines.length > 1) {
                                e.preventDefault();
                                addItems(lines);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                                e.preventDefault();
                                const text = draft;
                                setDraft("");
                                addItems([text]);
                            } else if (e.key === "Escape" && draft) {
                                e.stopPropagation();
                                setDraft("");
                            }
                        }}
                        sx={{ fontSize: 14 }}
                    />
                </Stack>
            )}
        </Box>
    );
};

interface RowProps {
    item: ChecklistItem;
    canEdit: boolean;
    onToggle: () => void;
    onRename: (text: string) => void;
    onRemove: () => void;
}

const ChecklistRow: React.FC<RowProps> = ({ item, canEdit, onToggle, onRename, onRemove }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.item_id, disabled: !canEdit });
    const [text, setText] = useState(item.text);
    React.useEffect(() => setText(item.text), [item.text]);

    return (
        <Stack
            ref={setNodeRef}
            direction="row"
            alignItems="center"
            spacing={0.25}
            sx={{
                transform: CSS.Transform.toString(transform),
                transition,
                position: "relative",
                zIndex: isDragging ? 1 : "auto",
                bgcolor: isDragging ? "background.paper" : "transparent",
                boxShadow: isDragging ? 3 : 0,
                borderRadius: 1,
                pr: 0.5,
                "&:hover .row-tools": { opacity: 1 },
            }}
        >
            <Box
                className="row-tools"
                {...attributes}
                {...listeners}
                aria-label="Drag to reorder"
                sx={{
                    display: "flex",
                    color: "text.disabled",
                    cursor: canEdit ? "grab" : "default",
                    opacity: 0,
                    visibility: canEdit ? "visible" : "hidden",
                    "&:focus-visible": { opacity: 1, outline: "2px solid", outlineColor: "primary.main", borderRadius: 0.5 },
                }}
            >
                <DragIndicator sx={{ fontSize: 18 }} />
            </Box>
            <Checkbox size="small" checked={item.is_done} disabled={!canEdit} onChange={onToggle} sx={{ p: 0.5 }} />
            <InputBase
                fullWidth
                multiline
                value={text}
                readOnly={!canEdit}
                onChange={(e) => setText(e.target.value.replace(/\n/g, ""))}
                onBlur={() => onRename(text)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        (e.target as HTMLElement).blur();
                    } else if (e.key === "Escape") {
                        e.stopPropagation();
                        setText(item.text);
                        (e.target as HTMLElement).blur();
                    }
                }}
                sx={{
                    fontSize: 14,
                    py: 0.25,
                    color: item.is_done ? "text.secondary" : "text.primary",
                    textDecoration: item.is_done ? "line-through" : "none",
                }}
            />
            {canEdit && (
                <Tooltip title="Remove">
                    <IconButton className="row-tools" size="small" onClick={onRemove} sx={{ opacity: 0, "&:focus-visible": { opacity: 1 } }}>
                        <Close sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    );
};
